import React, {useEffect, useRef, useState} from 'react';

/**
 * Ambient wash bloomed from the current cover art: the cover is rendered huge,
 * heavily blurred and slightly oversaturated behind the frosted glass, with a
 * dark gradient on top for legibility. When the track changes a fresh layer is
 * stacked on top and fades in (~400ms), then the stale layers are dropped — a
 * gentle cross-fade that re-keys to the new artwork.
 *
 * Approach 1 from the handoff (blur the cover image) — no canvas read, so no
 * CORS concerns with MusicKit artwork URLs.
 */
export default function AmbientBackdrop({coverUrl}) {
    const keyRef = useRef(0);
    const [layers, setLayers] = useState(coverUrl ? [{url: coverUrl, id: 0}] : []);
    const currentUrl = useRef(coverUrl || null);

    useEffect(() => {
        if (!coverUrl || coverUrl === currentUrl.current) {
            return;
        }
        currentUrl.current = coverUrl;
        keyRef.current += 1;
        const id = keyRef.current;

        setLayers((prev) => [...prev, {url: coverUrl, id}]);
        // Once the new layer has faded in, discard everything beneath it.
        const t = setTimeout(() => {
            setLayers([{url: coverUrl, id}]);
        }, 450);
        return () => clearTimeout(t);
    }, [coverUrl]);

    return (
        <div className="pointer-events-none absolute inset-0 overflow-hidden bg-ambient-base" aria-hidden="true">
            {layers.map((layer) => (
                <div
                    key={layer.id}
                    className="absolute inset-0 animate-ambientfade bg-cover bg-center scale-125 blur-3xl saturate-150"
                    style={{backgroundImage: `url("${layer.url}")`}}
                />
            ))}
            {/* Dark overlay for legibility (rgba(8,6,10,.3) → rgba(8,6,10,.6)) */}
            <div className="absolute inset-0 bg-gradient-to-b from-[#08060a4d] to-[#08060a99]"/>
        </div>
    );
}
